import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { TranslocoLocaleWrapperService } from 'app/fuse-template/core/transloco/translocoLocale.service';
import { copyLink } from 'app/helpers/copyLink';
import { formatDate } from 'app/helpers/formatDate';
import { sortCompare } from 'app/helpers/sortCompare';
import { DropDownItem } from 'app/interfaces/items/dropDownItem';
import { CreateGengiEignirReq } from 'app/interfaces/requests/Create/createGengiEignirReq';
import { CreateGengiReq } from 'app/interfaces/requests/Create/createGengiReq';
import { ExposedGengiSkraningReq } from 'app/interfaces/requests/Exposed/exposedGengiSkraningReq';
import { ListKriaDagsuppgjorRes } from 'app/interfaces/responses/List/listKriaDagsuppgjorRes';
import { ListMarketValueOverviewDivisionRes } from 'app/interfaces/responses/List/listMarketValueOverviewDivisionRes';
import { ReadKriaSidastaGengiRes } from 'app/interfaces/responses/Read/readKriaSidastaGengiRes';
import { GengiService } from 'app/services/api/gengi/gengi.service';
import { AlertsService } from 'app/services/system/alerts/alerts.service';
import { lsrDivisionsConstants } from 'common/constants/lsrDivisions.contants';
import moment from 'moment';
import { combineLatest } from 'rxjs';

@Component({
    selector: 'app-gengi-skra',
    templateUrl: './gengi-skra.component.html',
    styleUrls: ['./gengi-skra.component.scss'],
})
export class GengiSkraComponent implements OnInit {
    //#region INIT and Global Variables
    @ViewChild('tablePaginator') displayedDataPaginator: MatPaginator;
    headerColumns: string[] = [
        'symbol',
        'closingPriceToday',
        'closingPriceBefore',
        'marketValueToday',
        'marketValueBefore',
        'closingPriceDifference',
    ];

    formEnabled = true;
    form: UntypedFormGroup;
    markadsverdRes;
    dagsuppgjorEntry: ListKriaDagsuppgjorRes;
    displayedData: MatTableDataSource<ListMarketValueOverviewDivisionRes> = new MatTableDataSource();
    eignir: ListMarketValueOverviewDivisionRes[];
    thegarHefurVeridSkradGengi = false;
    gengiDagsinsSkrad: number;
    markadsverdDate: Date;
    sidastaSkradaGengi: ReadKriaSidastaGengiRes;
    mismunurFraSidustuSkraningu: number;
    skraGengiSimple: boolean = false;
    skraGengiEnabled: boolean = false;

    sereignarleidir: Array<DropDownItem> = [
        {
            value: lsrDivisionsConstants.lsr1,
            viewValue: 'Verðbréfaleið (LSR1)',
        },
        {
            value: lsrDivisionsConstants.lsr3,
            viewValue: 'Innlánaleið (LSR3)',
        }
    ];

    constructor(
        private _fuseConfirmationService: FuseConfirmationService,
        private _formBuilder: UntypedFormBuilder,
        private gengiService: GengiService,
        private formBuilder: UntypedFormBuilder,
        private alertsService: AlertsService,
        private localeService: TranslocoLocaleWrapperService
    ) {
        this.checkPage();
        this.initForm();
    }

    ngOnInit(): void { }

    checkPage() {
        if (window.location.href.includes('skra-gengi-ohad')) {
            this.skraGengiSimple = true;
        }
    }

    ngAfterViewInit(): void {
        this.displayedData.paginator = this.displayedDataPaginator;
    }

    public printOrSave() {
        window.print();
    }

    //#endregion

    //#region Http functions

    // Sækir síðasta skráða gengi í Kríu
    saekiSidastaSkradaGengi(division) {
        this.gengiService.kriaSidastaGengi({ division: division }).subscribe((gengi) => {
            this.sidastaSkradaGengi = gengi;
            this.calculateGengiDifference();
        });
    }

    // Sækir helstu upplýsingar sem þarf til að reikna út gengi.
    fetchData() {
        this.thegarHefurVeridSkradGengi = false;
        let fetchDagsuppgjor = this.gengiService.kriaDagsuppgjor({
            dagsGengi: this.form.get('dagsetning').value,
        });

        const selectedDate = new Date(this.form.get('dagsetning').value);
        let fetchMarkadsverd = this.gengiService.markadsvirdiLeidar({
            division: this.form.get('sereignarleid').value,
            dagsetning: this.getCalendarPreviousDayOrFriday(selectedDate),
        });

        let fetchMarketValueOverviewPortfolioSimple = this.gengiService.listMarketValueOverviewDivision({
            division: this.form.get('sereignarleid').value,
            dateToday: this.getCalendarPreviousDayOrFriday(selectedDate),
            dateBefore: this.getCalendarTwoDaysBeforeOrThursday(selectedDate),
        });

        let afterCall = (markadsverdRes, dagsuppgjorRes, marketValueOverviewPortfolioSimpleRes) => {
            this.markadsverdRes = markadsverdRes;

            this.dagsuppgjorEntry = null;
            if (this.form.get('sereignarleid').value == lsrDivisionsConstants.lsr1) {
                this.dagsuppgjorEntry = dagsuppgjorRes.filter((x) => x.audkenni_Kria == 'LSR1')[0];
            } else if (this.form.get('sereignarleid').value == lsrDivisionsConstants.lsr3) {
                this.dagsuppgjorEntry = dagsuppgjorRes.filter((x) => x.audkenni_Kria == 'LSR3')[0];
            }

            if (marketValueOverviewPortfolioSimpleRes.length > 0) {
                this.displayedData.data = marketValueOverviewPortfolioSimpleRes;
                this.eignir = marketValueOverviewPortfolioSimpleRes;
            } else {
                this.displayedData.data = [];
                this.eignir = [];
            }

            let skammstofun = '';
            if (this.form.get('sereignarleid').value == lsrDivisionsConstants.lsr1) {
                skammstofun = lsrDivisionsConstants.lsr1_short;
            } else if (this.form.get('sereignarleid').value == lsrDivisionsConstants.lsr3) {
                skammstofun = lsrDivisionsConstants.lsr3_short;
            }

            this.saekiSidastaSkradaGengi(skammstofun);

            this.gengiService
                .kriaSkradGengi({
                    skammstofun: skammstofun,
                    dagsGengi: moment(new Date(this.form.get('dagsetning').value)).format('YYYY-MM-DD'),
                })
                .subscribe((vidGengi) => {
                    if (vidGengi != null) {
                        this.gengiDagsinsSkrad = vidGengi.gengi;
                        this.thegarHefurVeridSkradGengi = true;
                    }
                });

            if (!this.skraGengiSimple) {
                if (this.dagsuppgjorEntry == null) {
                    alert('Ekkert dagsuppgjör í Kríu fannst fyrir valda dagsetningu');
                    this.form.get('vidskiptiGaerdagsins').setValue('');
                    this.form.get('utistandandiEiningar').setValue('');
                    this.form.get('markadsverd').setValue('');
                    this.form.get('gengi').setValue('');
                    this.mismunurFraSidustuSkraningu = null;
                    this.skraGengiEnabled = false;
                    return;
                } else {
                    this.form.get('vidskiptiGaerdagsins').setValue(this.localeService.localizeNumber(this.dagsuppgjorEntry.upphaed));
                    this.form.get('utistandandiEiningar').setValue(
                        this.localeService.localizeNumber(this.dagsuppgjorEntry.einingar_Alls, 'decimal', {
                            minimumFractionDigits: 4,
                            maximumFractionDigits: 4,
                        })
                    );
                    this.form
                        .get('markadsverd')
                        .setValue(this.localeService.localizeNumber(markadsverdRes.marketValue, 'decimal', { maximumFractionDigits: 0 }));
                    this.calculateGengi();
                    this.skraGengiEnabled = true;
                }
            }
        };

        combineLatest([fetchMarkadsverd, fetchDagsuppgjor, fetchMarketValueOverviewPortfolioSimple]).subscribe(
            ([markadsverdRes, dagsuppgjorRes, marketValueOverviewPortfolioSimpleRes]) => {
                afterCall(markadsverdRes, dagsuppgjorRes, marketValueOverviewPortfolioSimpleRes);
            }
        );
    }

    //#endregion

    //#region Helper functions

    formatDate(date) {
        return formatDate(date, false);
    }

    copyLink(v) {
        copyLink(v, 'Gengi afritað', this.alertsService);
    }

    toFixed(value, n) {
        return value.toFixed(n);
    }

    /**
     * Returns the previous day relative to `inputDate`.
     * If `inputDate` falls on a Saturday or Sunday, the function returns the previous Friday.
     */
    getCalendarPreviousDayOrFriday(inputDate: Date): Date {
        const result = new Date(inputDate); // clone the date
        const weekday = result.getDay(); // 0 = Sunday, 6 = Saturday

        if (weekday === 1) {
            // Monday -> go back 3 days to Friday
            result.setDate(result.getDate() - 3);
        } else if (weekday === 0) {
            // Sunday -> go back 2 days to Friday
            result.setDate(result.getDate() - 2);
        } else if (weekday === 6) {
            // Saturday -> go back 1 day to Friday
            result.setDate(result.getDate() - 1);
        } else {
            // Any other day -> simply go back 1 day
            result.setDate(result.getDate() - 1);
        }

        return result;
    }

    /**
     * Returns two days before `inputDate`.
     * If `inputDate` falls on a Saturday or Sunday, the function returns the previous Thursday.
     */
    getCalendarTwoDaysBeforeOrThursday(inputDate: Date): Date {
        const result = new Date(inputDate); // clone the date
        const weekday = result.getDay(); // 0 = Sunday, 6 = Saturday

        if (weekday === 1) {
            // Monday -> go back 4 days to Thursday
            result.setDate(result.getDate() - 4);
        } else if (weekday === 0) {
            // Sunday -> go back 3 days to Thursday
            result.setDate(result.getDate() - 3);
        } else {
            // For Saturday and weekdays -> go back 2 days
            // (For Saturday: -2 results in Thursday, matching the rule)
            result.setDate(result.getDate() - 2);
        }

        return result;
    }

    //#endregion

    //#region Form

    initForm() {
        this.form = this.formBuilder.group({
            sereignarleid: new UntypedFormControl('', [Validators.required]),
            dagsetning: new UntypedFormControl(new Date(new Date().toDateString()), [Validators.required]),
            gengi: new UntypedFormControl({
                value: '',
                disabled: !this.skraGengiSimple,
            }),
            utistandandiEiningar: new UntypedFormControl({
                value: '',
                disabled: true,
            }),
            vidskiptiGaerdagsins: new UntypedFormControl(''),
            vidbotVidMarkadsverd: new UntypedFormControl(''),
            markadsverd: new UntypedFormControl({ value: '', disabled: true }),
        });
    }

    revertAllChanges() {
        if (confirm('Ertu viss um að þú viljir hætta við?')) {
            this.displayedData.data = [];
            this.sidastaSkradaGengi = null;
            this.thegarHefurVeridSkradGengi = false;
            this.form.markAsPristine();
            this.initForm();
        }
    }

    onChangeDate(date) {
        this.form.get('dagsetning').setValue(date);
        this.form.get('vidbotVidMarkadsverd').setValue('');
        if (this.form.get('sereignarleid').value != '') {
            this.fetchData();
        }
        this.checkSkraGengiEnabled();
    }

    onChangeLeid(leid) {
        this.form.get('sereignarleid').setValue(leid);
        this.form.get('vidbotVidMarkadsverd').setValue('');
        if (this.form.get('dagsetning').value != '') {
            this.fetchData();
        }
        this.checkSkraGengiEnabled();
    }

    checkSkraGengiEnabled() {
        if (this.form.get('dagsetning').value != '' && this.form.get('sereignarleid').value != '') {
            this.skraGengiEnabled = true;
        } else {
            this.skraGengiEnabled = false;
        }
    }

    save() {
        let configForm = this._formBuilder.group({
            title: this.thegarHefurVeridSkradGengi ? 'Uppfæra gengi' : 'Skrá gengi',
            message:
                "Ertu viss um að þú viljir skrá gengi <strong>'" +
                this.form.get('gengi').value +
                "'</strong> fyrir dags. <strong>" +
                formatDate(this.form.get('dagsetning').value, false) +
                '</strong>, <strong>' +
                this.form.get('sereignarleid').value +
                '</strong>',
            icon: {
                show: true,
                name: 'heroicons_outline:exclamation',
                color: 'warn',
            },
            actions: {
                confirm: {
                    show: true,
                    label: 'Staðfesta',
                    color: 'warn',
                },
                cancel: {
                    show: true,
                    label: 'Hætta við',
                },
            },
            dismissible: true,
        });

        // Open the dialog and save the reference of it
        const dialogRef = this._fuseConfirmationService.open(configForm.value);

        // Subscribe to afterClosed from the dialog reference
        dialogRef.afterClosed().subscribe((result) => {
            if (result == 'confirmed') {
                let skammstofun = '';
                if (this.form.get('sereignarleid').value == lsrDivisionsConstants.lsr1) {
                    skammstofun = lsrDivisionsConstants.lsr1_short;
                } else if (this.form.get('sereignarleid').value == lsrDivisionsConstants.lsr3) {
                    skammstofun = lsrDivisionsConstants.lsr3_short;
                }

                let gengiReq: CreateGengiReq = {
                    dagsetning: this.form.get('dagsetning').value,
                    gengi: this.localeService.parseLocalizedNumber(this.form.get('gengi').value),
                    skammstofun: skammstofun,
                    additionToMarketValue: this.localeService.parseLocalizedNumber(this.form.get('vidbotVidMarkadsverd').value),
                    divisionUnits: this.localeService.parseLocalizedNumber(this.form.get('utistandandiEiningar').value),
                    marketValue: this.localeService.parseLocalizedNumber(this.form.get('markadsverd').value),
                    ydaTransactions: this.localeService.parseLocalizedNumber(this.form.get('vidskiptiGaerdagsins').value),
                };

                let eignirReq: CreateGengiEignirReq[] = [];

                if (!this.skraGengiSimple) {
                    this.eignir.forEach((e) => {
                        eignirReq.push({
                            closingPriceBefore: e.closingPriceBefore,
                            closingPriceDateBefore: e.closingPriceDateBefore,
                            closingPriceDateToday: e.closingPriceDateToday,
                            closingPriceDifference: e.closingPriceDifference,
                            closingPriceToday: e.closingPriceToday,
                            gengiId: null,
                            marketValueBefore: e.marketValueBefore,
                            marketValueToday: e.marketValueToday,
                            symbol: e.symbol,
                            symbolName: e.symbolName,
                            timeStamp: null,
                        });
                    });
                } else {
                    gengiReq = {
                        additionToMarketValue: null,
                        divisionUnits: null,
                        gengi: this.localeService.parseLocalizedNumber(this.form.get('gengi').value),
                        dagsetning: this.form.get('dagsetning').value,
                        marketValue: null,
                        skammstofun: skammstofun,
                        ydaTransactions: null,
                    };
                }

                let req: ExposedGengiSkraningReq = {
                    gengi: gengiReq,
                    eignir: eignirReq,
                };

                this.gengiService.SkraGengi(req).subscribe((res) => {
                    this.fetchData();
                });
            }
        });
    }

    //#endregion

    //#region Main functions

    // Reiknar gengi
    calculateGengi() {
        if (!this.skraGengiSimple) {
            let vidskiptiGaerdagsins = this.localeService.parseLocalizedNumber(this.form.get('vidskiptiGaerdagsins').value);
            let vidbot = this.localeService.parseLocalizedNumber(this.form.get('vidbotVidMarkadsverd').value);
            let totalAssetValue = this.markadsverdRes.marketValue + vidskiptiGaerdagsins + vidbot;
            let gengi =
                this.dagsuppgjorEntry.einingar_Alls == 0 || totalAssetValue == 0
                    ? this.localeService.localizeNumber(this.dagsuppgjorEntry.upphafs_gengi)
                    : this.localeService.localizeNumber((totalAssetValue / this.dagsuppgjorEntry.einingar_Alls).toFixed(0).toString());
            this.form.get('gengi').setValue(gengi);
            this.calculateGengiDifference();
        }
    }

    calculateGengiDifference() {
        try {
            this.mismunurFraSidustuSkraningu =
                this.localeService.parseLocalizedNumber(this.form.get('gengi').value) / +this.sidastaSkradaGengi.gengi;
        } catch { }
    }

    // Segir til um vikmörk fyrir alert fyrir markaðsverð
    checkGengiAlert(dif) {
        return (dif <= 0.95 || dif >= 1.05) && dif != 0;
    }

    checkTotalGengiAlert(dif) {
        return Math.abs(1 - dif) > 0.005;
    }

    //#endregion

    //#region Table Sort and Filter

    searchFilter(filterValue: string) {
        filterValue = filterValue.trim(); // Remove whitespace
        filterValue = filterValue.toLowerCase(); // MatTableDataSource defaults to lowercase matches
        this.displayedData.filter = filterValue;
    }

    sortData(sort: Sort) {
        const data = this.displayedData.data.slice();
        if (!sort.active || sort.direction === '') {
            this.displayedData.data = data;
            return;
        }

        this.displayedData.data = data.sort((a, b) => {
            const isAsc = sort.direction === 'asc';
            switch (sort.active) {
                case 'closingPriceBefore':
                    return sortCompare(a.closingPriceBefore, b.closingPriceBefore, isAsc);
                case 'closingPriceDateBefore':
                    return sortCompare(a.closingPriceDateBefore, b.closingPriceDateBefore, isAsc);
                case 'closingPriceDateToday':
                    return sortCompare(a.closingPriceDateToday, b.closingPriceDateToday, isAsc);
                case 'closingPriceToday':
                    return sortCompare(a.closingPriceToday, b.closingPriceToday, isAsc);
                case 'marketValueBefore':
                    return sortCompare(a.marketValueBefore, b.marketValueBefore, isAsc);
                case 'marketValueToday':
                    return sortCompare(a.marketValueToday, b.marketValueToday, isAsc);
                case 'symbol':
                    return sortCompare(a.symbol, b.symbol, isAsc);
                default:
                    return 0;
            }
        });
    }

    //#endregion

}
